import {
  Button,
  Col,
  Flex,
  Form,
  object,
  Row,
  string,
  TextField,
  TextFieldFormats,
} from '@appliedsystems/applied-design-system';
import { StoredPaymentMethod } from '@appliedsystems/payments-core';
import React, { useCallback, useState } from 'react';
import { AdyenCheckoutState } from 'src/util/adyen';
import * as yup from 'yup';
import { currencyMap } from '../../../src/constants/constants';
import { usePaymentsTranslation } from '../../../src/hooks/usePaymentsTranslation';
import { Locale } from '../../../src/store/Locale';
import { toIntlFormatHelper } from '../../../src/util/getAmountWithFees';
import { useMakePayment } from '../../hooks/useMakePayment';
import { CheckboxRecaptcha } from '../CheckboxRecaptcha/CheckboxRecaptcha';
import { ErrorMessage } from '../ErrorAlert/ErrorAlert';
import { useHppDataStore } from '../HostedPaymentPageContainer/useHppData';
import { useHppSessionStore } from '../HostedPaymentPageContainer/useHppSession';
import classes from './EftComponent.module.scss';

type Props = {
  disablePayNowButton: boolean;
  onPaymentSuccess: (state: AdyenCheckoutState) => void;
  onBack: () => void;
  setErrorMessage: (errorMessage: ErrorMessage | undefined) => void;
  storedPaymentMethods?: StoredPaymentMethod[]; // TODO: PAY-3805 - Implement store payment method functionality
};

export const EftComponent = ({ onPaymentSuccess, setErrorMessage, onBack, disablePayNowButton }: Props) => {
  const { makePayment } = useMakePayment(setErrorMessage);
  const { hppData } = useHppDataStore();
  const [recaptchaAlertOpen, setRecaptchaAlertOpen] = useState(false);
  const [onRecaptchaSuccess, setOnRecaptchaSuccess] = useState<(recaptchaToken: string) => Promise<void>>();

  const { hppSession: hppSessionDetail } = useHppSessionStore();
  const { locale } = Locale.useContainer();
  const { t } = usePaymentsTranslation();

  const { paymentTotal } = hppData;
  const storePaymentMethod = false; // TODO: PAY-3805 - Implement store payment method functionality

  const formSchema = React.useMemo(
    () =>
      object({
        accountHolderName: string().required(t('ACCOUNT_HOLDER_NAME_REQUIRED')),
        bankNumber: string().required(t('BANK_NUMBER_REQUIRED')).length(3, t('BANK_NUMBER_MUST_BE_3_DIGITS')),
        transitNumber: string().required(t('TRANSIT_NUMBER_REQUIRED')).length(5, t('TRANSIT_NUMBER_MUST_BE_5_DIGITS')),
        accountNumber: string()
          .required(t('ACCOUNT_NUMBER_REQUIRED'))
          .min(7, t('ACCOUNT_NUMBER_MUST_BE_AT_LEAST_7_DIGITS'))
          .max(12, t('ACCOUNT_NUMBER_MUST_BE_AT_MOST_12_DIGITS')),
        reEnterAccountNumber: string()
          .required(t('ACCOUNT_NUMBERS_MUST_MATCH'))
          .oneOf([yup.ref('accountNumber')], t('ACCOUNT_NUMBERS_MUST_MATCH')),
      }),
    [t],
  );

  const handleOnSubmit = useCallback(
    (values: yup.InferType<typeof formSchema>) => {
      if (!values) {
        return;
      }
      const { accountHolderName, bankNumber, transitNumber, accountNumber } = values;

      const state: AdyenCheckoutState = {
        data: {
          paymentMethod: {
            type: 'eft_directdebit_CA',
            bankAccountNumber: accountNumber,
            bankLocationId: transitNumber,
            bankCode: bankNumber,
            ownerName: accountHolderName,
          },
          storePaymentMethod,
        },
        isValid: true,
      };
      setOnRecaptchaSuccess(() => async (recaptchaToken: string) => {
        setRecaptchaAlertOpen(false);
        await makePayment(
          onPaymentSuccess,
          state.data.paymentMethod,
          recaptchaToken,
          state,
          hppData,
          storePaymentMethod,
          hppSessionDetail,
        );
      });
      setRecaptchaAlertOpen(true);
    },
    [hppData, hppSessionDetail, makePayment, onPaymentSuccess, storePaymentMethod],
  );

  return (
    <div className={classes.eftComponent}>
      <Form schema={formSchema} onSubmit={handleOnSubmit}>
        <Flex>
          <Row>
            <Col xs={12}>
              <div style={{ width: '100%' }}>
                <TextField
                  name="accountHolderName"
                  label={t('ACCOUNT_HOLDER_NAME')}
                  placeholder="J. Smith"
                  testId="accountHolderNameTest"
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <TextField
                  format={TextFieldFormats.NUMERIC}
                  maxLength={3}
                  name="bankNumber"
                  label={t('BANK_NUMBER')}
                  placeholder="3 digits"
                  testId="bankNumberTest"
                />
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <TextField
                  format={TextFieldFormats.NUMERIC}
                  maxLength={5}
                  name="transitNumber"
                  label={t('TRANSIT_NUMBER')}
                  placeholder="5 digits"
                  testId="transitNumberTest"
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <TextField
                  format={TextFieldFormats.NUMERIC}
                  maxLength={12}
                  name="accountNumber"
                  label={t('ACCOUNT_NUMBER')}
                  placeholder="7-12 digits"
                  testId="accountNumberTest"
                />
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ width: '100%' }}>
                <TextField
                  maxLength={12}
                  format={TextFieldFormats.NUMERIC}
                  name="reEnterAccountNumber"
                  label={t('RE_ENTER_ACCOUNT_NUMBER')}
                  testId="reEnterAccountNumberTest"
                />
              </div>
            </Col>
          </Row>
        </Flex>
        <Button
          type="primary"
          submit
          disabled={disablePayNowButton}
          className={`${classes.payButton} ${disablePayNowButton ? 'pay__button__disabled' : ''}`}
        >
          {t('PAY')} {toIntlFormatHelper(paymentTotal, currencyMap[locale], locale)}
        </Button>
        <Button type="tertiary" onClick={onBack} className={classes.backButton}>
          {t('BACK')}
        </Button>
      </Form>
      <CheckboxRecaptcha
        onError={() => {
          setErrorMessage(['RECAPTCHA_BROWSER_ERROR_WITH_ID']);
          setRecaptchaAlertOpen(false);
        }}
        onSuccess={onRecaptchaSuccess!}
        recaptchaAlertOpen={recaptchaAlertOpen}
        action="purchase"
      />
    </div>
  );
};
